import { Directive, HostListener, ElementRef, Input } from '@angular/core';

@Directive({
    selector: 'input[appNumericOnly]',
    standalone: true
})
export class NumericOnlyDirective {
    @Input() allowNegative = true;
    @Input() allowDecimal = true;

    private lastValidValue = '';

    constructor(private el: ElementRef<HTMLInputElement>) {
        this.lastValidValue = this.el.nativeElement.value || '';
    }

    // Prevent known invalid keystrokes for number inputs (e/E/+)
    @HostListener('keydown', ['$event'])
    onKeyDown(event: KeyboardEvent): void {
        // Check navigation keys
        if (this.shouldAllowNavigationKey(event)) {
            return;
        }

        // Block invalid characters
        if (this.shouldBlockInvalidCharacter(event)) {
            return;
        }

        const input = this.el.nativeElement;

        // Handle decimal point input
        if (event.key === '.') {
            this.handleDecimalInput(event, input);
            return;
        }

        // Handle minus sign input
        if (event.key === '-') {
            this.handleMinusInput(event, input);
            return;
        }
    }

    /**
     * Check if the key is a navigation key that should always be allowed
     */
    private shouldAllowNavigationKey(event: KeyboardEvent): boolean {
        // Allow control/meta shortcuts and navigation keys
        if (event.ctrlKey || event.metaKey) {
            return true;
        }

        const allowedKeys = new Set([
            'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
            'ArrowLeft', 'ArrowRight', 'Home', 'End'
        ]);

        return allowedKeys.has(event.key);
    }

    /**
     * Check if the character is invalid and should be blocked
     * Returns true if the event was handled (prevented)
     */
    private shouldBlockInvalidCharacter(event: KeyboardEvent): boolean {
        // Block scientific notation and plus sign which type="number" otherwise permits
        if (event.key === 'e' || event.key === 'E' || event.key === '+') {
            event.preventDefault();
            return true;
        }

        return false;
    }

    /**
     * Handle decimal point input with strict validation
     */
    private handleDecimalInput(event: KeyboardEvent, input: HTMLInputElement): void {
        if (!this.allowDecimal) {
            event.preventDefault();
            return;
        }

        const value = input.value;
        const selStart = input.selectionStart;
        const selEnd = input.selectionEnd;

        // If selection is known, allow '.' only if it doesn't result in multiple dots
        if (selStart !== null && selEnd !== null) {
            const before = value.slice(0, selStart);
            const after = value.slice(selEnd);
            const next = `${before}.${after}`;

            if ((before.includes('.') || after.includes('.')) && !value.includes('.')) {
                // This case is safe: replacing the existing '.'
                return;
            }

            if (next.indexOf('.') !== next.lastIndexOf('.')) {
                event.preventDefault();
                return;
            }

            return;
        }

        // When selection is unknown, allow '.' only if one is not already present
        if (value.includes('.')) {
            event.preventDefault();
        }
    }

    /**
     * Handle minus sign input: only a single leading '-' is allowed
     */
    private handleMinusInput(event: KeyboardEvent, input: HTMLInputElement): void {
        if (!this.allowNegative) {
            event.preventDefault();
            return;
        }

        const value = input.value;

        if (value.includes('-')) {
            event.preventDefault();
            return;
        }

        const selStart = input.selectionStart;
        const selEnd = input.selectionEnd;

        if (selStart !== null && selEnd !== null) {
            // Allow only if caret is at 0, or the selection starts at 0 (e.g., replacing entire value)
            const atStart = selStart === 0;
            if (!atStart) {
                event.preventDefault();
                return;
            }
            return;
        }

        // When selection is unknown (e.g., Safari reports null), allow '-' only if input is empty
        if (value.length > 0) {
            event.preventDefault();
        }
    }

    // Single source of truth: validate the entire value on every input
    @HostListener('input', ['$event'])
    onInput(event: Event): void {
        const input = this.el.nativeElement;
        const value = input.value;

        if (this.isValueValid(value)) {
            this.lastValidValue = value;
            return;
        }

        // Revert to last known good value if invalid
        input.value = this.lastValidValue;
        event.stopPropagation();
    }

    // Normalize incomplete values on blur (e.g., '-', '.', '-.') -> ''
    @HostListener('blur')
    onBlur(): void {
        const input = this.el.nativeElement;
        const value = input.value;

        if (!this.isFinalValueValid(value)) {
            input.value = '';
            this.lastValidValue = '';
            // Ensure Angular FormControl syncs with cleared value
            input.dispatchEvent(new Event('input', { bubbles: true }));
        }
    }

    // Prevent invalid paste creating disallowed patterns (e.g., internal '-')
    @HostListener('paste', ['$event'])
    onPaste(event: ClipboardEvent): void {
        const input = this.el.nativeElement;
        const pasteText = event.clipboardData?.getData('text') ?? '';

        const selStart = input.selectionStart;
        const selEnd = input.selectionEnd;
        let nextValue: string;

        if (selStart !== null && selEnd !== null) {
            const before = input.value.slice(0, selStart);
            const after = input.value.slice(selEnd);
            nextValue = `${before}${pasteText}${after}`;
        } else {
            // Fallback: assume append to end when selection is unknown
            nextValue = `${input.value}${pasteText}`;
        }

        if (!this.isValueValid(nextValue)) {
            event.preventDefault();
        }
    }

    // Block text drops which can bypass key filtering in some browsers
    @HostListener('drop', ['$event'])
    onDrop(event: DragEvent): void {
        event.preventDefault();
    }

    private isValueValid(value: string): boolean {
        if (value === '') return true; // allow clearing while typing

        // Build a permissive pattern to support in-progress typing states
        // Examples allowed when configured: '-', '.', '-.', '123', '-123', '123.', '123.45'
        const negative = this.allowNegative ? '-?' : '';

        // We want at most one dot and it can exist without following digits during typing
        const pattern = this.allowDecimal
            ? `^${negative}(?:\\d+\\.?\\d*|\\d*\\.?|\\.\\d*)$`
            : `^${negative}\\d*$`;

        return new RegExp(pattern).test(value);
    }

    private isFinalValueValid(value: string): boolean {
        if (value === '') return true;

        const negative = this.allowNegative ? '-?' : '';
        const pattern = this.allowDecimal
            ? `^${negative}\\d+(?:\\.\\d+)?$` // require at least one digit; optional fractional part
            : `^${negative}\\d+$`;

        return new RegExp(pattern).test(value);
    }
}

